/**
 * The L2 event-sourced reducer (MathToolImplementation.md §6.2; Architecture A6).
 *
 * `reduce(state, event)` is **pure and total**: it reads only its two arguments
 * (and, for the integrity guard, re-derives verdicts purely via the L1 kernel),
 * touches no time / randomness / locale / ambient state, and returns a new frozen
 * `State`. `replay(events)` folds the reducer over an event log from a fixed
 * initial state, so the same events in the same order rebuild the same state on
 * every run (INV-DET). The event log (`State.history`) is the authoritative input;
 * every registry and the snapshot are *derived caches* re-computed from it — the
 * persistence discipline behind INV-NOEQUIV ("store inputs, re-derive verdicts").
 *
 * Adding a constraint, claim, or edge happens *solely* through events, which keeps
 * the kernel sovereign and leaves the path open for Phase 2 domain-pack modules to
 * extend the config space without ever calling `core` (INV-ADD-NOT-ALTER).
 */
import { kernel } from '@progression/core';
import { createInitialState } from './state.js';
import { expertTransitionAllowed } from './integrity.js';
/** Copy a ReadonlyMap, apply one upsert, and return a fresh frozen map. */
function withEntry(map, key, value) {
    const next = new Map(map);
    next.set(key, value);
    return Object.freeze(next);
}
/** Read the (optional, Phase 2) community-flag ledger, defaulting to empty. */
function communityFlagsOf(state) {
    return state.communityFlags ?? Object.freeze(new Map());
}
/** Read the (optional, Phase 2) module-provenance map, defaulting to empty. */
function modulesOf(state) {
    return state.modules ?? Object.freeze(new Map());
}
/** The Community lane derived from a flag ledger entry: flags-exist iff non-empty. */
function communityLaneOf(notes) {
    return notes !== undefined && notes.length > 0 ? 'flags-exist' : 'none';
}
/** The active config implied by a status map (only `adopted-formal` is active). */
function activeConfig(statusMap) {
    return kernel.defaultConfig(statusMap);
}
/**
 * INV-LANE-3 guard. A `conflict-proven` Machine status is honoured only if it is
 * backed by a same-config refutation: the target System Claim must carry a
 * `configDigest` equal to the digest of the *current* active config, AND some
 * claim in the state must actually evaluate to `refuted` under that config (the
 * verdict is re-derived here, never trusted from a cache — INV-NOEQUIV). Absent
 * that proof, the record is refused: `conflict-proven` is never fabricated, and
 * `unverified`/`verified` records always pass through (INV-LANE-2).
 */
function sameConfigRefutationExists(state, sc) {
    if (sc.configDigest === undefined)
        return false;
    const config = activeConfig(state.statusMap);
    if (kernel.configDigest(config) !== sc.configDigest)
        return false; // not the same config
    for (const claim of state.claims.values()) {
        if (kernel.evaluate(claim, config).state === 'refuted')
            return true;
    }
    return false;
}
/**
 * Merge a `Partial<LaneStatus>` into a System Claim's lanes, keeping the three
 * lanes on independent tracks (never merged into a score):
 *   - **Machine** — the INV-LANE-3 guard applies: a `conflict-proven` value is
 *     only honoured when a same-config refutation exists, else the prior value is
 *     retained (an unbacked condemnation is refused — INV-LANE-2/3).
 *   - **Expert** — the ACM-style tiers; a transition is applied only if it is
 *     permitted by `expertTransitionAllowed` (Phase 3 policy: any distinct tier,
 *     self = idempotent no-op). A disallowed transition is refused (prior tier
 *     retained), so the lane only ever moves through legal transitions.
 *   - **Community** — NOT taken from the partial. The Community lane is driven
 *     solely by the persisted flag ledger (`flag-raised`/`flag-cleared`), so it
 *     has a single source of truth and flags genuinely persist. A `community`
 *     field on a `lane-status-recorded` event is ignored by design (logged: D2).
 */
function mergeLanes(state, sc, partial) {
    let machine = partial.machine ?? sc.lanes.machine;
    if (partial.machine === 'conflict-proven' && !sameConfigRefutationExists(state, sc)) {
        machine = sc.lanes.machine; // refuse an unbacked condemnation (INV-LANE-2/3)
    }
    let expert = sc.lanes.expert;
    if (partial.expert !== undefined && expertTransitionAllowed(expert, partial.expert)) {
        expert = partial.expert; // apply only a permitted Expert transition
    }
    return Object.freeze({
        machine,
        expert,
        community: communityLaneOf(communityFlagsOf(state).get(sc.id)), // ledger-driven
    });
}
function withSystemClaim(state, sc) {
    return withEntry(state.integrity, sc.id, Object.freeze(sc));
}
/**
 * Apply a domain-pack registration to the registries — the data side of
 * INV-ADD-NOT-ALTER. Constraints, claims and edges are added **only if their id
 * is not already present**; an id that already exists is left untouched (a module
 * can ADD to the config space but never ALTER what the kernel already governs).
 * The kernel is never called — registration is pure data. Provenance (which ids
 * this module actually added) is recorded per `moduleId`, merged across repeated
 * registrations.
 */
function applyModuleRegistration(state, reg) {
    let constraints = state.constraints;
    let statusMap = state.statusMap;
    let claims = state.claims;
    let edges = state.edges;
    const addedConstraints = [];
    const addedClaims = [];
    const addedEdges = [];
    for (const c of reg.constraints ?? []) {
        if (constraints.has(c.id))
            continue; // additive only (INV-ADD-NOT-ALTER)
        constraints = withEntry(constraints, c.id, { labelId: c.labelId, status: c.status });
        statusMap = withEntry(statusMap, c.id, c.status);
        addedConstraints.push(c.id);
    }
    for (const claim of reg.claims ?? []) {
        if (claims.has(claim.id))
            continue; // additive only
        claims = withEntry(claims, claim.id, claim);
        addedClaims.push(claim.id);
    }
    for (const edge of reg.edges ?? []) {
        if (edges.some((e) => e.id === edge.id))
            continue; // additive only
        edges = Object.freeze([...edges, edge]);
        addedEdges.push(edge.id);
    }
    const modules = modulesOf(state);
    const prior = modules.get(reg.moduleId);
    const provenance = {
        moduleId: reg.moduleId,
        constraintIds: Object.freeze([...(prior?.constraintIds ?? []), ...addedConstraints]),
        claimIds: Object.freeze([...(prior?.claimIds ?? []), ...addedClaims]),
        edgeIds: Object.freeze([...(prior?.edgeIds ?? []), ...addedEdges]),
    };
    return {
        ...state,
        constraints,
        statusMap,
        claims,
        edges,
        modules: withEntry(modules, reg.moduleId, Object.freeze(provenance)),
    };
}
/**
 * The pure, total reducer over the §6.2 `Event` union. Unknown-target events
 * (activating an undeclared constraint, promoting/flagging an absent id) are
 * no-ops on the registries but are still appended to `history` — the log records
 * the input faithfully even when it has no registry effect.
 */
function applyEvent(state, event) {
    switch (event.t) {
        case 'constraint-declared': {
            const entry = { labelId: event.labelId, status: event.status };
            return {
                ...state,
                constraints: withEntry(state.constraints, event.id, entry),
                statusMap: withEntry(state.statusMap, event.id, event.status),
            };
        }
        case 'constraint-activated': {
            const existing = state.constraints.get(event.id);
            if (existing === undefined)
                return state; // undeclared ⇒ no registry effect
            const entry = { labelId: existing.labelId, status: 'adopted-formal' };
            return {
                ...state,
                constraints: withEntry(state.constraints, event.id, entry),
                statusMap: withEntry(state.statusMap, event.id, 'adopted-formal'),
            };
        }
        case 'constraint-suspended': {
            const existing = state.constraints.get(event.id);
            if (existing === undefined)
                return state;
            const entry = { labelId: existing.labelId, status: 'suspended' };
            return {
                ...state,
                constraints: withEntry(state.constraints, event.id, entry),
                statusMap: withEntry(state.statusMap, event.id, 'suspended'),
            };
        }
        case 'edge-imported': {
            // Append, deduping by edge id so replay is idempotent on repeated imports.
            if (state.edges.some((e) => e.id === event.edge.id))
                return state;
            return { ...state, edges: Object.freeze([...state.edges, event.edge]) };
        }
        case 'claim-added': {
            return { ...state, claims: withEntry(state.claims, event.claim.id, event.claim) };
        }
        case 'profile-promoted': {
            const claim = state.claims.get(event.claimId);
            if (claim === undefined)
                return state; // unknown claim ⇒ no-op
            const updated = { ...claim, profile: event.to };
            return { ...state, claims: withEntry(state.claims, event.claimId, updated) };
        }
        case 'lane-status-recorded': {
            const sc = state.integrity.get(event.systemClaimId);
            if (sc === undefined)
                return state; // unknown System Claim ⇒ no-op
            const lanes = mergeLanes(state, sc, event.lanes);
            return { ...state, integrity: withSystemClaim(state, { ...sc, lanes }) };
        }
        case 'flag-raised': {
            const sc = state.integrity.get(event.systemClaimId);
            if (sc === undefined)
                return state; // unknown System Claim ⇒ no-op
            const ledger = communityFlagsOf(state);
            const current = ledger.get(event.systemClaimId) ?? [];
            if (current.includes(event.noteId))
                return state; // idempotent on a repeated flag
            const nextNotes = Object.freeze([...current, event.noteId]);
            // A community flag persists in the ledger; it never touches the independent
            // Machine or Expert lanes — only the ledger-derived Community lane moves.
            const lanes = Object.freeze({ ...sc.lanes, community: communityLaneOf(nextNotes) });
            return {
                ...state,
                integrity: withSystemClaim(state, { ...sc, lanes }),
                communityFlags: withEntry(ledger, event.systemClaimId, nextNotes),
            };
        }
        case 'flag-cleared': {
            const sc = state.integrity.get(event.systemClaimId);
            if (sc === undefined)
                return state; // unknown System Claim ⇒ no-op
            const ledger = communityFlagsOf(state);
            const current = ledger.get(event.systemClaimId) ?? [];
            if (!current.includes(event.noteId))
                return state; // nothing to clear ⇒ no-op
            const nextNotes = Object.freeze(current.filter((n) => n !== event.noteId));
            // The lane only falls to `none` once EVERY flag is cleared — flags persist.
            const lanes = Object.freeze({ ...sc.lanes, community: communityLaneOf(nextNotes) });
            return {
                ...state,
                integrity: withSystemClaim(state, { ...sc, lanes }),
                communityFlags: withEntry(ledger, event.systemClaimId, nextNotes),
            };
        }
        case 'module-registered': {
            return applyModuleRegistration(state, event.registration);
        }
        case 'computation-recorded': {
            // Record the computation as an immutable evidence fact, keyed by the claim
            // it bears on. Dedupe by record id so replay is idempotent (INV-DET). The
            // evidence map is created lazily — absent on pre-pass states. Evidence
            // informs the Machine lane (via lane()) but is NEVER a verdict (INV-CORE).
            const rec = event.record;
            const evidence = new Map(state.evidence ?? []);
            const prior = evidence.get(rec.claimRef) ?? [];
            if (prior.some((r) => r.id === rec.id))
                return state; // idempotent on a repeated id
            evidence.set(rec.claimRef, Object.freeze([...prior, rec]));
            return { ...state, evidence: Object.freeze(evidence) };
        }
        default: {
            // Exhaustiveness: every known Event variant is handled above (compile-time check).
            // At runtime, an unknown event — e.g. from an untrusted imported log that slipped past
            // the contract filter — is IGNORED (state returned unchanged) rather than becoming the
            // new state, so a crafted log cannot brick the store. (Security audit F4.)
            const _exhaustive = event;
            void _exhaustive;
            return state;
        }
    }
}
/**
 * The exported reducer (the `Reducer` contract). Wraps `applyEvent` and appends
 * the event to `history`, then freezes the result — the snapshot is a derived
 * cache over the (now-extended) authoritative log.
 */
export function reduce(state, event) {
    const next = applyEvent(state, event);
    return Object.freeze({
        ...next,
        history: Object.freeze([...state.history, event]),
    });
}
/**
 * Deterministic event-sourced rebuild (INV-DET). Folds `reduce` over the log from
 * a fresh initial state; the resulting `State` is a pure function of the event
 * sequence and the seeded integrity registry.
 */
export function replay(events, initial = createInitialState()) {
    let state = initial;
    for (const event of events)
        state = reduce(state, event);
    return state;
}
/** Convenience: derive the active config from a state's status map. */
export function configOf(state) {
    return activeConfig(state.statusMap);
}
//# sourceMappingURL=reducer.js.map